import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'
import { keepPreviousData } from '@tanstack/react-query'

import DeleteOutlinedIcon from '@ant-design/icons/DeleteOutlined'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import DialogTitle from '@components/_mui/DialogTitle'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import usePageParams from '@shared/hooks/src/usePageParams'
import { RoleGuard } from '@shared/providers/src/MeProvider'
import { handleError, UserRole } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import LinearProgress from '@components/LinearProgress'
import PaginationComp from '@components/Pagination'
import PatientAccessGuard from '@components/PatientAccessGuard'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'
import TableRowLink from '@components/TableRowLink'

import { usePatientEncounterRemove, usePatientEncounters, usePatientEncountersFilters } from './PatientEncounters.hooks'

export function Inline({ patientId, variant = 'outlined', canDelete, perPage, disableRowsPerPage }) {
  const { data, isRefreshing, isPending, confirmRemove } = useEncountersData({ patientId, perPage })

  return (
    <Stack spacing={1}>
      <Paper variant={variant} sx={{ overflow: 'hidden', position: 'relative' }}>
        <LinearProgress loading={isRefreshing} />
        <Content
          patientId={patientId}
          data={data}
          isLoading={isPending}
          canDelete={canDelete}
          confirmRemove={confirmRemove}
          perPage={perPage}
        />
      </Paper>
      <Pagination data={data} isLoading={isPending} perPage={perPage} disableRowsPerPage={disableRowsPerPage} />
    </Stack>
  )
}

export function Modal({ patientId, open, onClose, canDelete }) {
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="paper">
      <ModalContent patientId={patientId} onClose={onClose} canDelete={canDelete} />
    </Dialog>
  )
}

function ModalContent({ patientId, onClose, canDelete }) {
  const { data, isRefreshing, isPending, confirmRemove } = useEncountersData({ patientId })

  return (
    <>
      <DialogTitle onClose={onClose}>Encounters</DialogTitle>
      <LinearProgress loading={isRefreshing} />
      <DialogContent sx={{ p: 0 }}>
        <Content patientId={patientId} data={data} isLoading={isPending} canDelete={canDelete} confirmRemove={confirmRemove} />
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination data={data} isLoading={isPending} />
      </DialogActions>
    </>
  )
}

function useEncountersData({ patientId, perPage: perPageOverride }) {
  const [{ encountersSort }] = usePatientEncountersFilters()
  const [page, queryPerPage] = usePageParams({ id: 'encounters' })
  const perPage = perPageOverride ?? queryPerPage

  const query = usePatientEncounters(
    patientId,
    {
      order: encountersSort,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    { placeholderData: keepPreviousData }
  )

  const removeEncounter = usePatientEncounterRemove(patientId)

  const confirmRemove = useDialog({
    component: ConfirmationV2,
    props: ({ item: encounterId, close }) => ({
      variant: 'error',
      title: 'Delete Encounter?',
      description: 'Are you sure you want to delete this encounter?',
      onReject: () => close(),
      onConfirm: () =>
        removeEncounter
          .mutateAsync(encounterId)
          .then(() => {
            close()
            toast.success('Encounter has been deleted')
          })
          .catch(handleError),
    }),
  })

  return { ...query, confirmRemove }
}

function Content({ patientId, data, isLoading, canDelete, confirmRemove, perPage }) {
  const navigate = useNavigate()
  const [{ encountersSort }, updateFilters] = usePatientEncountersFilters()

  const showLoading = isLoading
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0
  const columns = canDelete ? 6 : 5

  return (
    <TableContainer>
      <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Encounter</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Diagnosis</TableCell>
            <TableCell sortDirection={encountersSort}>
              <TableSortLabel
                active
                direction={encountersSort}
                onClick={() => updateFilters({ encountersSort: encountersSort === 'desc' ? 'asc' : 'desc' })}
              >
                Date
              </TableSortLabel>
            </TableCell>
            {canDelete && (
              <RoleGuard allowed={[UserRole.Admin, UserRole.Provider]}>
                <PatientAccessGuard patientId={patientId}>
                  <TableCell align="right" />
                </PatientAccessGuard>
              </RoleGuard>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoading && <TableLoader columns={columns} items={perPage} />}
          {showEmpty && <TableEmpty columns={columns} />}
          {showData &&
            data?.map((encounter) => (
              <TableRowLink key={encounter.id} hover onClick={() => navigate(`/app/patients/${patientId}/encounters/${encounter.id}`)}>
                <TableCell>
                  <Typography copy>{encounter.id}</Typography>
                </TableCell>
                <TableCell>{encounter.description}</TableCell>
                <TableCell>{encounter.provider?.name}</TableCell>
                <TableCell>{encounter.diagnosis?.map((d) => d.name).join(', ')}</TableCell>
                <TableCell>{encounter.startOrCreateInProviderTimezone}</TableCell>
                {canDelete && (
                  <RoleGuard allowed={[UserRole.Admin, UserRole.Provider]}>
                    <PatientAccessGuard patientId={patientId}>
                      <TableCell align="right">
                        <IconButton
                          sx={{ m: -0.5 }}
                          disabled={!encounter.canDelete}
                          onClick={(e) => {
                            e.stopPropagation()
                            confirmRemove(encounter.id)
                          }}
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </TableCell>
                    </PatientAccessGuard>
                  </RoleGuard>
                )}
              </TableRowLink>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function Pagination({ data, isLoading, perPage: perPageOverride, disableRowsPerPage }) {
  const [page, queryPerPage, setPageParams] = usePageParams({ id: 'encounters' })
  const perPage = perPageOverride ?? queryPerPage

  return (
    <PaginationComp
      id="encounters"
      loading={isLoading}
      last={data?.length < perPage}
      page={page}
      perPage={perPage}
      setPageParams={(params) => setPageParams(perPageOverride ? { page: params.page } : params)}
      disableRowsPerPage={disableRowsPerPage}
    />
  )
}
